import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ChevronLeft, ChevronRight, LucideAngularModule } from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { IsEmptyDirective } from '@shared/directives/is-empty/is-empty.directive';

/**
 * @internal
 *
 * A lightweight internal pagination component used for table views.
 *
 * Handles basic "Previous" and "Next" pagination logic and emits changes to the parent.
 * Can optionally render in full-screen layouts.
 */
@Component({
  selector: 'app-table-pagination',
  imports: [
    LucideAngularModule,
    TranslatePipe,
    NgClass,
    IsEmptyDirective
  ],
  templateUrl: './table-pagination.component.html',
  styleUrl: './table-pagination.component.css'
})
export class TablePaginationComponent {
  /**
   * Number of items displayed per page.
   */
  @Input() public pageSize!: number;

  /**
   * Total number of items in the dataset.
   */
  @Input() public dataLength!: number;

  /**
   * Current active page number (1-indexed).
   */
  @Input() public currentPage: number = 1;

  /**
   * Whether the pagination is being displayed in a full-screen context (optional).
   */
  @Input() public fullScreen: boolean = false;

  /**
   * Emits the updated page number when pagination changes.
   */
  @Output() public currentPageChange: EventEmitter<number> = new EventEmitter()

  /**
   * Lucide icon for the previous page button.
   */
  protected readonly ChevronLeft = ChevronLeft;

  /**
   * Lucide icon for the next page button.
   */
  protected readonly ChevronRight = ChevronRight;

  /**
   * Navigates to the previous page if not already on the first page.
   * Emits the new page number.
   */
  public previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.currentPageChange.emit(this.currentPage);
    }
  }

  /**
   * Navigates to the next page if there are more items to display.
   * Emits the new page number.
   */
  public nextPage(): void {
    if (this.currentPage * this.pageSize < this.dataLength) {
      this.currentPage++;
      this.currentPageChange.emit(this.currentPage);
    }
  }
}
